import { useEffect, useCallback, useRef } from 'react';

// =============================================
// DEVELOPER BYPASS — DEBUG MODE
// =============================================
// To DISABLE the console-killer protections while debugging,
// open the browser console BEFORE the hook activates and run:
//
//   localStorage.setItem('Object(b.jsx)', 'b.Fragment');
//
// To RE-ENABLE protections:
//
//   localStorage.removeItem('Object(b.jsx)');
//

// =============================================
const DEV_MODE_KEY = 'Object(b.jsx)';
const DEV_MODE_PASSPHRASE = 'b.Fragment';

function isDevModeActive() {
    try {
        return localStorage.getItem(DEV_MODE_KEY) === DEV_MODE_PASSPHRASE;
    } catch (_) {
        return false;
    }
}

export const useConsolekiller = (token) => {


    const devtoolsDetectedRef = useRef(false);

    const handleDevToolsDetected = useCallback(() => {
        // If dev-mode bypass is active, skip all destructive actions
        if (isDevModeActive()) return;

        if (devtoolsDetectedRef.current) return;
        devtoolsDetectedRef.current = true;

        try {
            document.body.innerHTML = '';
            document.title = '';
        } catch (_) { /* ignore */ }

        // eslint-disable-next-line no-restricted-globals
        self.location.replace(
            window.location.protocol + '//' + window.location.host + window.location.pathname
        );
    }, []);

    useEffect(() => {
        // =============================================
        // 1. Debugger-based detection (primary method)
        // =============================================
        // Incrementar el umbral a 300ms para evitar falsos positivos por lentitud del CPU en móviles
        const THRESHOLD_MS = 300;

        function debuggerCheck() {
            const t0 = performance.now();
            // eslint-disable-next-line no-debugger
            debugger;
            const t1 = performance.now();
            if (t1 - t0 > THRESHOLD_MS) {
                handleDevToolsDetected();
            }
        }

        const debuggerIntervalId = setInterval(debuggerCheck, 5500);

        // =============================================
        // 2. Window size diff detection
        //    When DevTools is docked, the outer window
        //    dimensions differ from the inner viewport.
        // =============================================
        const SIZE_THRESHOLD = 160;

        function sizeCheck() {
            // Omitir en móviles: los teclados virtuales y barras de direcciones cambian el innerHeight drásticamente.
            const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || (window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
            if (isMobile) return;

            // En algunos navegadores móviles/embebidos outerWidth es 0.
            if (window.outerWidth === 0 || window.outerHeight === 0) return;

            const widthDiff = Math.abs(window.outerWidth - window.innerWidth);
            const heightDiff = Math.abs(window.outerHeight - window.innerHeight);
            
            if (widthDiff > SIZE_THRESHOLD || heightDiff > SIZE_THRESHOLD) {
                handleDevToolsDetected();
            }
        }

        const sizeIntervalId = setInterval(sizeCheck, 5000);

        // =============================================
        // 3. Console method trapping
        //    Override console methods to detect usage.
        // =============================================
        const originalConsole = {};
        const consoleMethods = ['log', 'warn', 'error', 'info', 'debug', 'table', 'trace', 'dir', 'dirxml', 'group', 'groupEnd', 'time', 'timeEnd', 'assert', 'count', 'clear'];

        consoleMethods.forEach((method) => {
            originalConsole[method] = console[method];
            try {
                // eslint-disable-next-line no-console
                console[method] = function () {
                    // silently swallow
                };
            } catch (_) { /* some environments freeze console */ }
        });

        // Periodic console.clear to remove any output
        const clearIntervalId = setInterval(() => {
            try {
                if (originalConsole.clear) originalConsole.clear.call(console);
            } catch (_) { /* ignore */ }
        }, 6000);

        // =============================================
        // 4. toString / getter trap (Firebug-style)
        //    DevTools triggers toString when inspecting.
        // =============================================
        const trapElement = new Image();
        Object.defineProperty(trapElement, 'id', {
            get: function () {
                handleDevToolsDetected();
                return '';
            },
        });

        // Periodically push the trap to console (DevTools
        // will invoke the getter when rendering it)
        const trapIntervalId = setInterval(() => {
            try {
                if (originalConsole.log) {
                    originalConsole.log.call(console, '%c', trapElement);
                }
            } catch (_) { /* ignore */ }
        }, 6000);

        // =============================================
        // 5. Block right-click context menu
        // =============================================
        const handleContextMenu = (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };

        // =============================================
        // 6. Block keyboard shortcuts (Windows/Linux + Mac)
        // =============================================
        const handleKeyDown = (e) => {
            const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
            const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

            // F12
            if (e.key === 'F12') {
                return disableEvent(e);
            }

            // Ctrl/Cmd + Shift + I  (DevTools)
            if (ctrlOrCmd && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
                return disableEvent(e);
            }

            // Ctrl/Cmd + Shift + J  (Console)
            if (ctrlOrCmd && e.shiftKey && (e.key === 'J' || e.key === 'j')) {
                return disableEvent(e);
            }

            // Ctrl/Cmd + Shift + C  (Element picker)
            if (ctrlOrCmd && e.shiftKey && (e.key === 'C' || e.key === 'c')) {
                return disableEvent(e);
            }

            // Ctrl/Cmd + U  (View source)
            if (ctrlOrCmd && (e.key === 'U' || e.key === 'u')) {
                return disableEvent(e);
            }

            // Ctrl/Cmd + S  (Save page)
            if (ctrlOrCmd && (e.key === 'S' || e.key === 's') && !e.shiftKey) {
                return disableEvent(e);
            }

            // Mac: Cmd + Option + I / J / C  (DevTools on macOS)
            if (isMac && e.metaKey && e.altKey) {
                if (e.key === 'I' || e.key === 'i' ||
                    e.key === 'J' || e.key === 'j' ||
                    e.key === 'C' || e.key === 'c') {
                    return disableEvent(e);
                }
            }

            // Ctrl + Shift + P  (Command palette in DevTools, prevent opening)
            if (ctrlOrCmd && e.shiftKey && (e.key === 'P' || e.key === 'p')) {
                return disableEvent(e);
            }

            return true;
        };

        function disableEvent(e) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
        }

        // =============================================
        // 7. Block drag & drop of page (prevents saving)
        // =============================================
        const handleDragStart = (e) => {
            e.preventDefault();
        };

        // =============================================
        // 8. Disable text selection (copy protection)
        // =============================================
        const handleSelectStart = (e) => {
            // Allow selection in input and textarea
            const tag = e.target?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea') return;
            e.preventDefault();
        };

        // =============================================
        // 9. Block copy/paste of page content
        // =============================================
        const handleCopy = (e) => {
            const tag = e.target?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea') return;
            e.preventDefault();
        };

        // =============================================
        // Register event listeners
        // =============================================
        document.addEventListener('contextmenu', handleContextMenu, true);
        document.addEventListener('keydown', handleKeyDown, true);
        document.addEventListener('dragstart', handleDragStart, true);
        document.addEventListener('selectstart', handleSelectStart, true);
        document.addEventListener('copy', handleCopy, true);

        // =============================================
        // Cleanup on unmount
        // =============================================
        return () => {
            clearInterval(debuggerIntervalId);
            clearInterval(sizeIntervalId);
            clearInterval(clearIntervalId);
            clearInterval(trapIntervalId);

            document.removeEventListener('contextmenu', handleContextMenu, true);
            document.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('dragstart', handleDragStart, true);
            document.removeEventListener('selectstart', handleSelectStart, true);
            document.removeEventListener('copy', handleCopy, true);

            // Restore console methods
            consoleMethods.forEach((method) => {
                try {
                    if (originalConsole[method]) {
                        // eslint-disable-next-line no-console
                        console[method] = originalConsole[method];
                    }
                } catch (_) { /* ignore */ }
            });
        };
    }, [handleDevToolsDetected]);
};
